import React from "react"
import { graphql } from "gatsby"
import { CodeBlock, H4 } from "@fastly/consistently"

import MDXWrapper from "~templates/mdx"

import Fiddle from "~components/fiddle/fiddle"
import Link from "~components/link/link"
import AsideTopicSeries from "~components/aside-topic-series/aside-topic-series"
import TabbedPanels from "~components/tabbed-panels/tabbed-panels"

import PlainTextProvider from "~providers/plaintext"

const Template = ({
  data: {
    examplesJson: { implementations, topics },
  },
}) => {
  const vclImpl = implementations.vcl
  const ecpImpls = Object.entries(implementations)
    .filter(([k, val]) => k !== "vcl" && val)
    .map(([k, obj]) => ({ ...obj, lang: k }))
  return (
    <>
      {vclImpl && (
        <>
          <H4 as="h2">
            <Link className="anchor" to="#vcl" id="vcl" />
            VCL
          </H4>
          <p>
            Use this solution in your <Link to="/guides/vcl">VCL service</Link> (click RUN below to test this solution
            or&nbsp;
            <Link to={`https://fiddle.fastly.dev/fiddle/${vclImpl.fiddleID}/clone`}>clone it to make changes</Link>
            ):
          </p>
          <Fiddle id={vclImpl.fiddleID} />
        </>
      )}

      {ecpImpls.length > 0 && (
        <>
          <H4 as="h2" id="rust">
            <Link className="anchor" to="#compute" id="compute" />
            Compute
          </H4>
          <p>
            Use this solution in your <Link to="/guides/compute">Compute service</Link>:
          </p>
          <TabbedPanels tabs={ecpImpls.map((impl) => ({ key: impl.lang }))}>
            {ecpImpls.map((impl) => {
              if (impl.src?.length) {
                return (
                  <PlainTextProvider
                    key={impl.lang}
                    src={impl.src}
                    render={(content, filename) => {
                      if (filename === "Cargo.toml") {
                        content = content.replace(/^.*(\[dependencies\])/s, "$1") // TODO: use a TOML parser?
                      } else if (filename === "package.json") {
                        const { dependencies } = JSON.parse(content)
                        if (!dependencies) {
                          return null
                        }
                        content = JSON.stringify({ dependencies })
                      }
                      return (
                        <CodeBlock key={filename} title={filename} language={impl.lang}>
                          {content}
                        </CodeBlock>
                      )
                    }}
                  />
                )
              } else if (impl.fiddleID) {
                return <Fiddle key={impl.fiddleID} id={impl.fiddleID} border={0} />
              }
              return null
            })}
          </TabbedPanels>
        </>
      )}

      {Boolean(topics) && <AsideTopicSeries topics={topics} />}
    </>
  )
}

export const query = graphql`
  query ($id: String!) {
    examplesJson(id: { eq: $id }) {
      title
      slug
      tags
      topics
      implementations {
        go {
          fiddleID
        }
        javascript {
          expressly
          fiddleID
        }
        rust {
          fiddleID
        }
        vcl {
          fiddleID
          tokens
        }
      }
      summary
    }
  }
`

const Wrapper = (props) => {
  return MDXWrapper({
    ...props,
    Template,
    pageContext: {
      ...props.data.examplesJson,
      withComments: true,
      editUrl:
        "https://github.com/fastly/Developer-Portal/blob/production/docs/contributing-content/CONTRIBUTING-code-examples.md",
    },
  })
}

export default Wrapper
